import { useTransferBillingToClientMutation } from "@api/billingTransfer.api";
import { Organization } from "@common/types/organization";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { Form, FormikProvider, useFormik } from "formik";
import { FC } from "react";
import { Button } from "@design-system/Button/Button";
import {
    TransferBillingFormSchema,
    transferBillingFormSchema,
} from "../useTransferBillingOptions";
import actionsStyles from "../ClientActions.module.scss";
import { ClientDetailsRow } from "../views/clientViewConfig";
import styles from "./TransferBillingToClient.module.scss";
import { NoOtherClientAdminsNotice } from "./NoOtherClientAdminsNotice";

interface TransferBillingToClientModalProps {
    client: ClientDetailsRow;
    onClose: () => void;
}

export const TransferBillingToClientModal: FC<
    TransferBillingToClientModalProps
> = ({ client, onClose }) => {
    const { toast } = useToaster();
    const activeOrganization = useActiveOrganization() as Organization;
    const transferBillingToClientMutation =
        useTransferBillingToClientMutation();

    const newPrimaryAdminOptions =
        client.members
            ?.filter((member) => member.organizationId === null)
            .map((member) => ({
                value: member.userId,
                label: member.preferredName ?? "",
            })) ?? [];

    const formik = useFormik<TransferBillingFormSchema>({
        initialValues: {
            newPrimaryAdminId:
                newPrimaryAdminOptions.length > 0
                    ? newPrimaryAdminOptions[0].value
                    : 0,
        },
        validate: validateFormikWithZodSchema(transferBillingFormSchema),
        onSubmit: submitHelper({
            handler: async (values) => {
                const newPrimaryAdmin = client.members?.find(
                    (assignment) =>
                        assignment.userId === values.newPrimaryAdminId,
                );

                if (!newPrimaryAdmin) {
                    return;
                }

                await transferBillingToClientMutation.mutateAsync({
                    workspaceId: client.id,
                    newPrimaryAdminId: values.newPrimaryAdminId,
                    organizationId: activeOrganization.id,
                });
                toast(
                    `Billing ownership transferred to ${newPrimaryAdmin?.preferredName}`,
                );
                onClose();
            },
        }),
    });

    return (
        <>
            <StandardModalHeader className={actionsStyles.modalHeader}>
                Transfer billing ownership
            </StandardModalHeader>
            <StandardModalBody className={actionsStyles.modalBody}>
                <FormikProvider value={formik}>
                    <Form>
                        {newPrimaryAdminOptions.length > 0 ? (
                            <section className={styles.content}>
                                <p>
                                    Your client will be notified to take over
                                    billing for their subscription.
                                </p>

                                <p>
                                    Select the client user to become the new
                                    subscription admin:
                                </p>

                                <FormikField<number>
                                    name="newPrimaryAdminId"
                                    label="Billing owner"
                                    control="button"
                                    className={styles.billingOwnerField}
                                >
                                    <Picker.Select
                                        items={newPrimaryAdminOptions}
                                        value={
                                            newPrimaryAdminOptions.some(
                                                (option) =>
                                                    option.value ===
                                                    formik.values
                                                        .newPrimaryAdminId,
                                            )
                                                ? formik.values
                                                      .newPrimaryAdminId
                                                : null
                                        }
                                        onValueChange={(v) => {
                                            if (v !== null) {
                                                formik.setFieldValue(
                                                    "newPrimaryAdminId",
                                                    v,
                                                );
                                            }
                                        }}
                                        disabled={
                                            newPrimaryAdminOptions.length <= 1
                                        }
                                        placeholder="Select admin"
                                        trigger={
                                            <Picker.Button
                                                variant="primary"
                                                size="xl"
                                            />
                                        }
                                    />
                                </FormikField>
                            </section>
                        ) : (
                            <section className={styles.content}>
                                <NoOtherClientAdminsNotice />
                            </section>
                        )}

                        <footer className={styles.footer}>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={onClose}
                                disabled={formik.isSubmitting}
                                size="xl"
                            >
                                Cancel
                            </Button>
                            {newPrimaryAdminOptions.length > 0 && (
                                <ButtonWithLoader
                                    type="submit"
                                    variant="primary"
                                    disabled={
                                        !formik.isValid || formik.isSubmitting
                                    }
                                    loading={formik.isSubmitting}
                                    size="xl"
                                >
                                    Transfer billing
                                </ButtonWithLoader>
                            )}
                        </footer>
                    </Form>
                </FormikProvider>
            </StandardModalBody>
        </>
    );
};
